import { getAccessToken } from "../../utils/auth";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import * as XLSX from "xlsx";
import axios from "../../utils/api.js";

const MallDetails = () => {
  const [mallData, setMallsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMall = async () => {
      const token = getAccessToken();
      try {
        const response = await axios.get("/get-mall-data", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMallsData(response.data);
      } catch (error) {
        console.error("Error fetching malldata:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMall();
  }, []);

  // Function to auto-size columns based on the longest data entry
  const autoFitColumns = (jsonData) => {
    return Object.keys(jsonData[0] || {}).map((key) => ({
      wch:
        Math.max(
          key.length, // Minimum width should fit the header text
          ...jsonData.map((row) => (row[key] ? row[key].toString().length : 0)) // Longest cell
        ) + 5, // Add some extra padding
    }));
  };

  // Function to download Excel
  const handleDownloadExcel = () => {
    if (mallData.length === 0) {
      alert("No data available to download.");
      return;
    }
    const mallInfoData = mallData.map((mall, index) => ({
      "S.No": index + 1,
      "Mall Name": mall.basicMallInformation?.mallName || "N/A",
      "Mall Type": mall.basicMallInformation?.mallType || "N/A",
      "Total Floor Area (sq. ft.)":
        mall.basicMallInformation?.totalFloorArea || "N/A",
      "Number of Floors": mall.basicMallInformation?.numberOfFloors || "N/A",
      "Number of Stores": mall.basicMallInformation?.numberOfStores || "N/A",
      Accreditations: Array.isArray(mall.basicMallInformation?.accreditations) // Check if it's an array
        ? mall.basicMallInformation.accreditations.join(", ") // Join if it's an array
        : mall.basicMallInformation?.accreditations || "N/A", // Use the value directly if it's not an array
    }));

    const mallCollectionData = mallData.map((mall, index) => ({
      "S.No": index + 1,
      "Mall Name": mall.basicMallInformation?.mallName || "N/A",
      "Date of Data Collection": mall.dataCollectionDetails
        ?.dateOfDataCollection
        ? new Date(
            mall.dataCollectionDetails.dateOfDataCollection
          ).toLocaleDateString()
        : "N/A",
      "Data Collected By": Array.isArray(
        mall.dataCollectionDetails?.dataCollectedBy
      ) // Check if it's an array
        ? mall.dataCollectionDetails.dataCollectedBy.join(", ") // Join if it's an array
        : mall.dataCollectionDetails?.dataCollectedBy || "N/A", // Use the value directly if it's not an array
      "Data Source": mall.dataCollectionDetails?.dataSource || "N/A",
    }));

    const mallLayoutData = mallData.map((mall, index) => ({
      "S.No": index + 1,
      "Mall Name": mall.basicMallInformation?.mallName || "N/A",
      "Average Daily Footfall":
        mall.mallLayoutTrafficFlow?.averageDailyFootfall || "N/A",
      "Peak Hours of Footfall":
        mall.mallLayoutTrafficFlow?.peakHoursOfFootfall?.join(", ") || "N/A",
      "Total Entrances/Exits":
        mall.mallLayoutTrafficFlow?.totalEntrancesExits || "N/A",
      "Escalators & Elevators":
        mall.mallLayoutTrafficFlow?.numberOfEscalatorsElevators || "N/A",
      "Total Food Courts": mall.mallLayoutTrafficFlow?.totalFoodCourts || "N/A",
      "Number of Restrooms":
        mall.mallLayoutTrafficFlow?.numberOfRestrooms || "N/A",
      "Seating Lounge": mall.mallLayoutTrafficFlow?.seatingLoungeAreas
        ? "Yes"
        : "No",
      "Cinema Theater": mall.mallLayoutTrafficFlow?.cinemaTheater
        ? "Yes"
        : "No",
      "Kids Play Area": mall.mallLayoutTrafficFlow?.kidsPlayArea ? "Yes" : "No",
    }));

    const cleaningData = mallData.map((mall, index) => ({
      "S.No": index + 1,
      "Mall Name": mall.basicMallInformation?.mallName || "N/A",
      "Total Cleaning Budget":
        mall.cleaningDisinfectionPractices?.totalCleaningBudget || "N/A",
      "Common Areas Cost":
        mall.cleaningDisinfectionPractices?.disinfectionCostcommonAreas ||
        "N/A",
      "Food Court Cost":
        mall.cleaningDisinfectionPractices?.disinfectionCostfoodCourt || "N/A",
      "Restrooms Cost":
        mall.cleaningDisinfectionPractices?.disinfectionCostrestrooms || "N/A",
      "Seating Areas Cost":
        mall.cleaningDisinfectionPractices?.disinfectionCostseatingAreas ||
        "N/A",
      "Disinfection Methods":
        mall.cleaningDisinfectionPractices?.currentDisinfectionMethods?.join(
          ", "
        ) || "N/A",
      "Disinfection Frequency":
        mall.cleaningDisinfectionPractices
          ?.disinfectionFrequencyPerAreaPerDay || "N/A",
      "Manual Disinfection Time": mall.cleaningDisinfectionPractices
        ?.manualDisinfectionTime
        ? `${mall.cleaningDisinfectionPractices.manualDisinfectionTime} mins`
        : "N/A",
      "Disinfection Challenges":
        mall.cleaningDisinfectionPractices?.disinfectionChallenges?.join(
          ", "
        ) || "N/A",
    }));

    const healthSafetyData = mallData.map((mall, index) => ({
      "S.No": index + 1,
      "Mall Name": mall.basicMallInformation?.mallName || "N/A",
      "Regulatory Standards":
        mall.healthSafetyCompliance?.regulatoryStandards || "N/A",
      "Audit Frequency":
        mall.healthSafetyCompliance?.auditFrequency?.join(", ") || "N/A",
      "Visitor Complaints": mall.healthSafetyCompliance?.visitorComplaints
        ?.hasComplaints
        ? "Yes"
        : "No",
      "Complaint Details":
        mall.healthSafetyCompliance?.visitorComplaints?.details?.join(", ") ||
        "N/A",
    }));

    const staffAutomationData = mallData.map((mall, index) => ({
      "S.No": index + 1,
      "Mall Name": mall.basicMallInformation?.mallName || "N/A",
      "Housekeeping Staff":
        mall.staffAutomationReadiness?.housekeepingStaff || "N/A",
      "Cleaning Staff Shift Hours": mall.staffAutomationReadiness
        ?.cleaningStaffShiftHours
        ? `${mall.staffAutomationReadiness.cleaningStaffShiftHours} hrs`
        : "N/A",
      "PPE Usage": mall.staffAutomationReadiness?.ppeUsage ? "Yes" : "No",
      "Automation Readiness": mall.staffAutomationReadiness?.automationReadiness
        ? "Ready"
        : "Not Ready",
      "Concerns About UV-C Usage": mall.staffAutomationReadiness
        ?.concernsUV_CUsage
        ? "Yes"
        : "No",
    }));

    const timeCostEfficiencyData = mallData.map((mall, index) => ({
      "S.No": index + 1,
      "Mall Name": mall.basicMallInformation?.mallName || "N/A",
      "Manual Disinfection Time (per day)": mall.timeCostEfficiencyUVCRobot
        ?.manualDisinfectionTimePerDay
        ? `${mall.timeCostEfficiencyUVCRobot.manualDisinfectionTimePerDay} hrs`
        : "N/A",
      "Disinfection Consumable Cost": mall.timeCostEfficiencyUVCRobot
        ?.disinfectionConsumableCost
        ? `$${mall.timeCostEfficiencyUVCRobot.disinfectionConsumableCost}`
        : "N/A",
      "Labor Cost": mall.timeCostEfficiencyUVCRobot?.laborCost
        ? `$${mall.timeCostEfficiencyUVCRobot.laborCost}`
        : "N/A",
      "Complaint Cost": mall.timeCostEfficiencyUVCRobot?.complaintCost
        ? `$${mall.timeCostEfficiencyUVCRobot.complaintCost}`
        : "N/A",
      "Electricity Consumption": mall.timeCostEfficiencyUVCRobot
        ?.electricityConsumption
        ? `${mall.timeCostEfficiencyUVCRobot.electricityConsumption} kWh`
        : "N/A",
    }));

    const integrationCustomizationData = mallData.map((mall, index) => ({
      "S.No": index + 1,
      "Mall Name": mall.basicMallInformation?.mallName || "N/A",
      "Existing Mall IT System": mall.integrationCustomizationNeeds
        ?.existingMallITSystem
        ? "Yes"
        : "No",
      "Integration Needs":
        mall.integrationCustomizationNeeds?.integrationNeeds?.join(", ") ||
        "N/A",
      "Preferred Financial Model":
        mall.integrationCustomizationNeeds?.preferredFinancialModel || "N/A",
      "Customization Needs":
        mall.integrationCustomizationNeeds?.customizationNeeds?.join(", ") ||
        "N/A",
    }));

    const demonstrationNextStepsData = mallData.map((mall, index) => ({
      "S.No": index + 1,
      "Mall Name": mall.basicMallInformation?.mallName || "N/A",
      "Interest in Demo": mall.demonstrationNextSteps?.interestInDemo
        ? "Yes"
        : "No",
      "Pilot Project Readiness": mall.demonstrationNextSteps
        ?.pilotProjectReadiness
        ? "Yes"
        : "No",
      "Key Decision Maker":
        mall.demonstrationNextSteps?.keyDecisionMaker || "N/A",
      "Decision-Making Timeline":
        mall.demonstrationNextSteps?.decisionMakingTimeline?.join(", ") ||
        "N/A",
    }));

    const additionalNotesData = mallData.map((mall, index) => ({
      "S.No": index + 1,
      "Mall Name": mall.basicMallInformation?.mallName || "N/A",
      "Additional Notes": mall.additionalnotes?.note || "N/A",
    }));
    const wb = XLSX.utils.book_new();

    // Convert JSON to Worksheet
    const ws1 = XLSX.utils.json_to_sheet(mallInfoData);
    const ws2 = XLSX.utils.json_to_sheet(mallCollectionData);
    const ws3 = XLSX.utils.json_to_sheet(mallLayoutData);
    const ws4 = XLSX.utils.json_to_sheet(cleaningData);
    const ws5 = XLSX.utils.json_to_sheet(healthSafetyData);
    const ws6 = XLSX.utils.json_to_sheet(staffAutomationData);
    const ws7 = XLSX.utils.json_to_sheet(timeCostEfficiencyData);
    const ws8 = XLSX.utils.json_to_sheet(integrationCustomizationData);
    const ws9 = XLSX.utils.json_to_sheet(demonstrationNextStepsData);
    const ws10 = XLSX.utils.json_to_sheet(additionalNotesData);

    // Auto-size columns based on content
    ws1["!cols"] = autoFitColumns(mallInfoData);
    ws2["!cols"] = autoFitColumns(mallCollectionData);
    ws3["!cols"] = autoFitColumns(mallLayoutData);
    ws4["!cols"] = autoFitColumns(cleaningData);
    ws5["!cols"] = autoFitColumns(healthSafetyData);
    ws6["!cols"] = autoFitColumns(staffAutomationData);
    ws7["!cols"] = autoFitColumns(timeCostEfficiencyData);
    ws8["!cols"] = autoFitColumns(integrationCustomizationData);
    ws9["!cols"] = autoFitColumns(demonstrationNextStepsData);
    ws10["!cols"] = autoFitColumns(additionalNotesData);

    // Set row height for better readability
    ws1["!rows"] = [{ hpx: 25 }];
    ws2["!rows"] = [{ hpx: 25 }];
    ws3["!rows"] = [{ hpx: 25 }];
    ws4["!rows"] = [{ hpx: 25 }];
    ws5["!rows"] = [{ hpx: 25 }];
    ws6["!rows"] = [{ hpx: 25 }];
    ws7["!rows"] = [{ hpx: 25 }];
    ws8["!rows"] = [{ hpx: 25 }];
    ws9["!rows"] = [{ hpx: 25 }];
    ws10["!rows"] = [{ hpx: 25 }];

    // Append sheets to workbook
    XLSX.utils.book_append_sheet(wb, ws1, "Mall Info");
    XLSX.utils.book_append_sheet(wb, ws2, "Mall Collection");
    XLSX.utils.book_append_sheet(wb, ws3, "Layout&Traffic Flow");
    XLSX.utils.book_append_sheet(wb, ws4, "Cleaning & Disinfection ");
    XLSX.utils.book_append_sheet(wb, ws5, "Health & Safety Compliance");
    XLSX.utils.book_append_sheet(wb, ws6, "Staff Automation Readiness");
    XLSX.utils.book_append_sheet(wb, ws7, "Time Cost Efficiency UV-C Robot");
    XLSX.utils.book_append_sheet(wb, ws8, "Integration & Customization");
    XLSX.utils.book_append_sheet(wb, ws9, "Demonstration Next Steps");
    XLSX.utils.book_append_sheet(wb, ws10, "Additional Notes");

    // Get the current date
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(currentDate.getDate()).padStart(2, "0");

    // Format the date as dd-mm-yyyy
    const formattedDate = `${day}-${month}-${year}`;

    // Create the filename with the current date
    const filename = `MallData${formattedDate}.xlsx`;

    // Write and download Excel file
    XLSX.writeFile(wb, filename);
  };

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  if (loading) {
    return (
      <div
        style={{
          ...pageStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>
          Loading...
        </span>
      </div>
    );
  }
  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Mall-info Data
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          View and download mall data
        </p>
      </div>

      <button style={downloadButton} onClick={handleDownloadExcel}>
        📥 Download Excel
      </button>

      {mallData.length === 0 ? (
        <div style={noDataStyle}>
          <h1>No Data Available</h1>
        </div>
      ) : (
        <>
          <h2 style={heading}>Basic Mall Information</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Mall Name</th>
                  <th style={thStyle}>Mall Type</th>
                  <th style={thStyle}>Total Floor Area (sq. ft.)</th>
                  <th style={thStyle}>Number of Floors</th>
                  <th style={thStyle}>Number of Stores</th>
                  <th style={thStyle}>Accreditations</th>
                </tr>
              </thead>
              <tbody>
                {mallData.map((mall, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.mallName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.mallType || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.totalFloorArea || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.numberOfFloors || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.numberOfStores || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.accreditations?.join(", ") ||
                        "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Mall Data Collection Details</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Mall Name</th>
                  <th style={thStyle}>Date of Data Collection</th>
                  <th style={thStyle}>Data Collected By</th>
                  <th style={thStyle}>Data Source</th>
                </tr>
              </thead>
              <tbody>
                {mallData.map((mall, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.mallName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.dataCollectionDetails.dateOfDataCollection
                        ? new Date(
                            mall.dataCollectionDetails.dateOfDataCollection
                          ).toLocaleDateString("en-GB")
                        : "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.dataCollectionDetails.dataCollectedBy}
                    </td>
                    <td style={tdStyle}>
                      {mall.dataCollectionDetails.dataSource || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Mall Layout & Traffic Flow</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Mall Name</th>
                  <th style={thStyle}>Average Daily Footfall</th>
                  <th style={thStyle}>Peak Hours of Footfall</th>
                  <th style={thStyle}>Total Entrances/Exits</th>
                  <th style={thStyle}>Escalators & Elevators</th>
                  <th style={thStyle}>Total Food Courts</th>
                  <th style={thStyle}>Number of Restrooms</th>
                  <th style={thStyle}>Seating Lounge</th>
                  <th style={thStyle}>Cinema Theater</th>
                  <th style={thStyle}>Kids Play Area</th>
                </tr>
              </thead>
              <tbody>
                {mallData.map((mall, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.mallName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.mallLayoutTrafficFlow.averageDailyFootfall || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.mallLayoutTrafficFlow.peakHoursOfFootfall?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.mallLayoutTrafficFlow.totalEntrancesExits || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.mallLayoutTrafficFlow.numberOfEscalatorsElevators ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.mallLayoutTrafficFlow.totalFoodCourts || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.mallLayoutTrafficFlow.numberOfRestrooms || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.mallLayoutTrafficFlow.seatingLoungeAreas
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {mall.mallLayoutTrafficFlow.cinemaTheater ? "Yes" : "No"}
                    </td>
                    <td style={tdStyle}>
                      {mall.mallLayoutTrafficFlow.kidsPlayArea ? "Yes" : "No"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Cleaning & Disinfection Practices</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Mall Name</th>
                  <th style={thStyle}>Total Cleaning Budget</th>
                  <th style={thStyle}>Common Areas Cost</th>
                  <th style={thStyle}>Food Court Cost</th>
                  <th style={thStyle}>Restrooms Cost</th>
                  <th style={thStyle}>Seating Areas Cost</th>
                  <th style={thStyle}>Disinfection Methods</th>
                  <th style={thStyle}>Disinfection Frequency</th>
                  <th style={thStyle}>Manual Disinfection Time</th>
                  <th style={thStyle}>Disinfection Challenges</th>
                </tr>
              </thead>
              <tbody>
                {mallData.map((mall, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.mallName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.cleaningDisinfectionPractices.totalCleaningBudget ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.cleaningDisinfectionPractices
                        .disinfectionCostcommonAreas || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.cleaningDisinfectionPractices
                        .disinfectionCostfoodCourt || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.cleaningDisinfectionPractices
                        .disinfectionCostrestrooms || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.cleaningDisinfectionPractices
                        .disinfectionCostseatingAreas || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.cleaningDisinfectionPractices.currentDisinfectionMethods?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.cleaningDisinfectionPractices
                        .disinfectionFrequencyPerAreaPerDay || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.cleaningDisinfectionPractices
                        .manualDisinfectionTime || "N/A"}{" "}
                      mins
                    </td>
                    <td style={tdStyle}>
                      {mall.cleaningDisinfectionPractices.disinfectionChallenges?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Health & Safety Compliance</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Mall Name</th>
                  <th style={thStyle}>Regulatory Standards</th>
                  <th style={thStyle}>Audit Frequency</th>
                  <th style={thStyle}>Visitor Complaints</th>
                  <th style={thStyle}>Complaint Details</th>
                </tr>
              </thead>
              <tbody>
                {mallData.map((mall, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.mallName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.healthSafetyCompliance.regulatoryStandards || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.healthSafetyCompliance.auditFrequency?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.healthSafetyCompliance.visitorComplaints
                        .hasComplaints
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {mall.healthSafetyCompliance.visitorComplaints.details?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Staff Automation Readiness</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Mall Name</th>
                  <th style={thStyle}>Housekeeping Staff</th>
                  <th style={thStyle}>Cleaning Staff Shift Hours</th>
                  <th style={thStyle}>PPE Usage</th>
                  <th style={thStyle}>Automation Readiness</th>
                  <th style={thStyle}>Concerns About UV-C Usage</th>
                </tr>
              </thead>
              <tbody>
                {mallData.map((mall, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.mallName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.staffAutomationReadiness.housekeepingStaff || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.staffAutomationReadiness.cleaningStaffShiftHours ||
                        "N/A"}{" "}
                      hrs
                    </td>
                    <td style={tdStyle}>
                      {mall.staffAutomationReadiness.ppeUsage ? "Yes" : "No"}
                    </td>
                    <td style={tdStyle}>
                      {mall.staffAutomationReadiness.automationReadiness
                        ? "Ready"
                        : "Not Ready"}
                    </td>
                    <td style={tdStyle}>
                      {mall.staffAutomationReadiness.concernsUV_CUsage
                        ? "Yes"
                        : "No"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Time & Cost Efficiency of UVC Robot</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Mall Name</th>
                  <th style={thStyle}>Manual Disinfection Time (per day)</th>
                  <th style={thStyle}>Disinfection Consumable Cost</th>
                  <th style={thStyle}>Labor Cost</th>
                  <th style={thStyle}>Complaint Cost</th>
                  <th style={thStyle}>Electricity Consumption</th>
                </tr>
              </thead>
              <tbody>
                {mallData.map((mall, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.mallName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.timeCostEfficiencyUVCRobot
                        .manualDisinfectionTimePerDay || "N/A"}{" "}
                      hrs
                    </td>
                    <td style={tdStyle}>
                      $
                      {mall.timeCostEfficiencyUVCRobot
                        .disinfectionConsumableCost || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      ${mall.timeCostEfficiencyUVCRobot.laborCost || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      ${mall.timeCostEfficiencyUVCRobot.complaintCost || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.timeCostEfficiencyUVCRobot.electricityConsumption ||
                        "N/A"}{" "}
                      kWh
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Integration & Customization Needs</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Mall Name</th>
                  <th style={thStyle}>Existing Mall IT System</th>
                  <th style={thStyle}>Integration Needs</th>
                  <th style={thStyle}>Preferred Financial Model</th>
                  <th style={thStyle}>Customization Needs</th>
                </tr>
              </thead>
              <tbody>
                {mallData.map((mall, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.mallName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.integrationCustomizationNeeds.existingMallITSystem
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {mall.integrationCustomizationNeeds.integrationNeeds?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.integrationCustomizationNeeds
                        .preferredFinancialModel || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.integrationCustomizationNeeds.customizationNeeds?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Demonstration & Next Steps</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Mall Name</th>
                  <th style={thStyle}>Interest in Demo</th>
                  <th style={thStyle}>Pilot Project Readiness</th>
                  <th style={thStyle}>Key Decision Maker</th>
                  <th style={thStyle}>Decision-Making Timeline</th>
                </tr>
              </thead>
              <tbody>
                {mallData.map((mall, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {mall.basicMallInformation.mallName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.demonstrationNextSteps.interestInDemo
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {mall.demonstrationNextSteps.pilotProjectReadiness
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {mall.demonstrationNextSteps.keyDecisionMaker || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.demonstrationNextSteps.decisionMakingTimeline?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>* Additional Notes *</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Mall Name</th>
                  <th style={thStyle}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {mallData.map((mall, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                    {mall.basicMallInformation.mallName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {mall.additionalnotes.note || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// Internal CSS styles
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "10px",
};

const thStyle = {
  backgroundColor: "#007bff",
  color: "#fff",
  padding: "10px",
  textAlign: "center",
  border: "1px solid #ddd",
};
const tableContainerStyle = {
  width: "100%",
  overflowX: "auto", // Enables horizontal scrolling
};
const tdStyle = {
  padding: "10px",
  border: "1px solid #ddd",
  textAlign: "center",
};

const heading = {
  fontSize: "24px",
  color: "black",
};

const downloadButton = {
  position: "absolute",
  top: "20px",
  right: "20px",
  backgroundColor: "#007bff",
  color: "white",
  border: "none",
  padding: "10px 15px",
  cursor: "pointer",
  borderRadius: "5px",
  fontSize: "14px",
};
const noDataStyle = {
  textAlign: "center",
  marginTop: "20px",
  fontSize: "20px",
  color: "#888",
};
export default MallDetails;
